import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  ACQUISITION_PRODUCT_QUERY_PARAM,
  AcquisitionProductId,
  acquisitionProductQueryValue,
  normalizeAcquisitionProduct,
} from './acquisitionProducts';

export interface UseAcquisitionProductResult {
  product: AcquisitionProductId;
  selectProduct: (next: AcquisitionProductId) => void;
}

/**
 * The selected product lives in the URL rather than in component state, so a
 * tab is shareable, bookmarkable and restored by Back and Forward. Selections
 * are pushed, never replaced: moving between products is navigation the
 * operator can undo.
 */
export const useAcquisitionProduct = (): UseAcquisitionProductResult => {
  const [searchParams, setSearchParams] = useSearchParams();

  const product = useMemo(
    () =>
      normalizeAcquisitionProduct(
        searchParams.get(ACQUISITION_PRODUCT_QUERY_PARAM)
      ),
    [searchParams]
  );

  const selectProduct = useCallback(
    (next: AcquisitionProductId) => {
      if (next === product) return;

      const params = new URLSearchParams(searchParams);
      const value = acquisitionProductQueryValue(next);
      if (value === null) {
        params.delete(ACQUISITION_PRODUCT_QUERY_PARAM);
      } else {
        params.set(ACQUISITION_PRODUCT_QUERY_PARAM, value);
      }

      setSearchParams(params, { replace: false });
    },
    [product, searchParams, setSearchParams]
  );

  return { product, selectProduct };
};
